// screens-articles.jsx — Artikelregister (price book) editor. Edit à-priser, add/remove
// custom articles. Rates flow straight into reports/invoices via the shared ARTICLES list.
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
const { useState: useStateAr } = React;

function nextArticleCode(articles) {
  const codes = new Set(articles.map((a) => a.code));
  let n = 1;
  while (codes.has('ART' + n)) n++;
  return 'ART' + n;
}

function ArticlesScreen({ articles, setArticles, onBack }) {
  const [editing, setEditing] = useStateAr(null); // {code?, name, rate, unit, kind, icon, custom, isNew}

  const openNew = () => setEditing({ name: '', rate: '0', unit: 'st', kind: 'cost', icon: 'box', custom: true, isNew: true });
  const openEdit = (a) => setEditing({ ...a, rate: String(a.rate ?? 0), isNew: false });

  const save = () => {
    const name = (editing.name || '').trim() || 'Ny artikel';
    const rate = Math.max(0, parseFloat(String(editing.rate).replace(',', '.')) || 0);
    const unit = (editing.unit || '').trim() || 'st';
    if (editing.isNew) {
      const code = nextArticleCode(articles);
      setArticles([...articles, { code, name, unit, rate, kind: 'cost', icon: 'box', custom: true }]);
    } else {
      setArticles(articles.map((a) => (a.code === editing.code ? { ...a, name, unit, rate } : a)));
    }
    setEditing(null);
  };
  const removeArt = () => {
    setArticles(articles.filter((a) => a.code !== editing.code));
    setEditing(null);
  };

  return (
    <>
      <div className="gv-app">
        <AppHeader onBack={onBack} subtitle="Inställningar" title="Artikelregister"
          right={<button className="gv-iconbtn" aria-label="Ny artikel" onClick={openNew}><Icon name="plus" size={22} stroke={2.2} /></button>} />
        <div className="gv-scroll">
          <Card style={{ marginTop: 4, background: 'var(--surface-2)', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            Dina artiklar och à-priser. Priserna fylls i automatiskt när du rapporterar och fakturerar — håll särskilt <b style={{ color: 'var(--ink)' }}>arbetstid</b> och <b style={{ color: 'var(--ink)' }}>reskostnad</b> uppdaterade.
          </Card>

          <SecLabel action="Ny artikel" onAction={openNew}>Artiklar</SecLabel>
          <div className="gv-list">
            {articles.map((a) => (
              <div key={a.code} className="gv-lrow is-tap" onClick={() => openEdit(a)}>
                <div className="gv-lrow-ic"><Icon name={a.icon || 'box'} size={18} stroke={1.9} /></div>
                <div className="gv-lrow-tt">
                  <b style={{ fontSize: 15 }}>{a.name}</b>
                  <span className="gv-mono">{a.code} · per {a.unit}</span>
                </div>
                <div style={{ textAlign: 'right', marginRight: 6 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 14 }}>{a.rate ? kr(a.rate) : 'à-pris'}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>/{a.unit}</div>
                </div>
                <Icon name="chevR" size={16} stroke={2} color="var(--ink-3)" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <Sheet open={!!editing} onClose={() => setEditing(null)} title={editing && editing.isNew ? 'Ny artikel' : 'Redigera artikel'} maxH="82%">
        {editing && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Namn" value={editing.name} onChange={(v) => setEditing((e) => ({ ...e, name: v }))} placeholder="t.ex. Arbetstid" />
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1.5 }}>
                  <Field label="À-pris" value={editing.rate} onChange={(v) => setEditing((e) => ({ ...e, rate: v }))} mono suffix="kr" />
                </div>
                <div style={{ flex: 1 }}>
                  <Field label="Enhet" value={editing.unit} onChange={(v) => setEditing((e) => ({ ...e, unit: v }))} placeholder="st" />
                </div>
              </div>
              {editing.kind === 'material' && <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>0 kr = à-pris (anges per rapport).</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
              <Btn variant="primary" icon="check" full onClick={save}>Spara artikel</Btn>
              {editing.custom && !editing.isNew && <Btn variant="danger" icon="trash" full onClick={removeArt}>Ta bort artikel</Btn>}
            </div>
          </>
        )}
      </Sheet>
    </>
  );
}

export { ArticlesScreen };
